/**
 * Workflows list — /settings/custom-modules/$key/workflows
 *
 * Lists every workflow defined on this module with trigger summary,
 * enabled toggle, and click-through to the editor. "New workflow"
 * button starts a blank editor on a fresh route.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { AlertTriangle, Plus, Workflow, Zap, ZapOff } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/workflows/')({
  component: WorkflowsListPage,
})

function WorkflowsListPage() {
  const { key: moduleKey } = Route.useParams()
  const queryClient = useQueryClient()

  const { data: workflows, isLoading } = useQuery(
    orpc.modules.custom.workflows.list.queryOptions({ input: { moduleKey } }),
  )

  const updateMutation = useMutation(
    orpc.modules.custom.workflows.update.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.modules.custom.workflows.list.queryKey({
            input: { moduleKey },
          }),
        })
      },
      onError: (err) => toast.error(err.message),
    }),
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Workflows</h2>
          <p className="text-sm text-muted-foreground">
            Automate reactions to record events with triggers + actions.
          </p>
        </div>
        <Link
          to="/settings/custom-modules/$key/workflows/new"
          params={{ key: moduleKey }}
        >
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New workflow
          </Button>
        </Link>
      </div>

      {isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !workflows || workflows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Workflow className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              No workflows yet. Create one to react to record events automatically.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {workflows.map((wf) => {
            const trigger = wf.trigger as {
              type: string
              entityKey?: string
              conditions?: unknown[]
            }
            const stepCount = (wf.steps as unknown[]).length
            return (
              <Link
                key={wf.id}
                to="/settings/custom-modules/$key/workflows/$workflowKey"
                params={{ key: moduleKey, workflowKey: wf.key }}
              >
                <Card className="hover:bg-muted/50 transition-colors cursor-pointer">
                  <CardHeader className="flex flex-row items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-base flex items-center gap-2">
                        {wf.name}
                        {wf.enabled ? (
                          <Badge variant="default" className="text-xs">
                            <Zap className="h-3 w-3 mr-1" /> Enabled
                          </Badge>
                        ) : wf.disabledReason ? (
                          <Badge variant="destructive" className="text-xs">
                            <AlertTriangle className="h-3 w-3 mr-1" /> Auto-paused
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="text-xs">
                            <ZapOff className="h-3 w-3 mr-1" /> Disabled
                          </Badge>
                        )}
                      </CardTitle>
                      {wf.description && (
                        <p className="text-sm text-muted-foreground mt-1">{wf.description}</p>
                      )}
                      {wf.disabledReason && (
                        <p className="text-xs text-destructive mt-2 flex items-start gap-1">
                          <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                          <span>
                            {wf.disabledReason}. Edit the steps or reinstall the module to
                            re-enable.
                          </span>
                        </p>
                      )}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        updateMutation.mutate({
                          moduleKey,
                          key: wf.key,
                          enabled: !wf.enabled,
                        })
                      }}
                    >
                      {wf.enabled ? 'Disable' : 'Enable'}
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>
                        <span className="font-mono">{trigger.type}</span>
                        {trigger.entityKey && (
                          <>
                            {' '}
                            on <span className="font-mono">{trigger.entityKey}</span>
                          </>
                        )}
                      </span>
                      <span>•</span>
                      <span>
                        {stepCount} {stepCount === 1 ? 'step' : 'steps'}
                      </span>
                      {trigger.conditions && trigger.conditions.length > 0 && (
                        <>
                          <span>•</span>
                          <span>
                            {trigger.conditions.length}{' '}
                            {trigger.conditions.length === 1 ? 'condition' : 'conditions'}
                          </span>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
